// Mode app (webview Capacitor) côté client : détection de l'app native et du
// mode app servi par le serveur.
//
// Deux notions distinctes, à ne pas confondre :
//   - la MISE EN PAGE mobile (classe webview-mode sur <html>/<body>) : posée
//     par le serveur dans l'app, et par le client sur tout écran de 1024 px ou
//     moins, hors landing ;
//   - le MODE APP côté serveur (?webview=true reconduit sur chaque lien) :
//     réservé à la vraie app. Un navigateur mobile ordinaire ne doit pas y
//     basculer (pages restreintes, footer caché, consentement GA refusé).

type CapacitorLike = {
  isNativePlatform?: () => boolean
  getPlatform?: () => string
  Plugins?: Record<string, any>
}

const capacitor = (): CapacitorLike | undefined =>
  typeof window === 'undefined' ? undefined : (window as unknown as { Capacitor?: CapacitorLike }).Capacitor

/** Capacitor signale une plateforme native (pas le user-agent : voir isNativeApp). */
export function isCapacitorNative(): boolean {
  const cap = capacitor()
  return !!cap && (!!cap.isNativePlatform?.() || ['ios', 'android'].includes(cap.getPlatform?.() || ''))
}

export function isNativeApp(): boolean {
  if (isCapacitorNative()) return true
  const ua = navigator.userAgent || ''
  return /northvaneapp|capacitor|ionic/i.test(ua)
}

/** Le serveur rend cette page en mode app (?webview=true, user-agent de l'app). */
export function isAppMode(): boolean {
  if (typeof document === 'undefined') return false
  if (document.body?.dataset.serverWebview === 'true') return true
  const params = new URLSearchParams(window.location.search)
  if (params.get('webview') === 'true' || params.get('app') === 'true') return true
  return isNativeApp()
}

/** Mise en page mobile (webview-mode), app ou petit écran. */
export function isWebviewLayout(): boolean {
  if (typeof document === 'undefined') return false
  return isAppMode() || document.body.classList.contains('webview-mode') || window.innerWidth <= 1024
}

export function isMobileOrTablet(): boolean {
  return window.innerWidth <= 1024
}

const INTERNAL_DOMAINS = ['northvane.app', 'www.northvane.app', 'localhost', '127.0.0.1']

export function isInternalLink(url: string): boolean {
  try {
    const host = new URL(url, window.location.origin).hostname.toLowerCase()
    return INTERNAL_DOMAINS.some((d) => host === d || host.endsWith(`.${d}`))
  } catch {
    return true
  }
}

export function openExternal(url: string): void {
  const browser = capacitor()?.Plugins?.Browser
  if (browser) browser.open({ url })
  else window.open(url, '_system')
}

/** preserve_webview_param / preserveWebviewInUrl : ajoute webview=true en mode app. */
export function withWebviewParam(href: string): string {
  if (!isAppMode()) return href
  try {
    const url = new URL(href, window.location.origin)
    if (url.searchParams.has('webview') || url.searchParams.has('app')) return href
    url.searchParams.set('webview', 'true')
    return url.origin === window.location.origin ? url.pathname + url.search + url.hash : url.toString()
  } catch {
    return href + (href.includes('?') ? '&' : '?') + 'webview=true'
  }
}

export function scrollToTop(): void {
  if (!isWebviewLayout()) return
  if ('scrollRestoration' in history) history.scrollRestoration = 'manual'
  requestAnimationFrame(() => {
    window.scrollTo(0, 0)
    document.documentElement.scrollTop = 0
    document.body.scrollTop = 0
  })
}

function isInsideFixedLayer(element: Element): boolean {
  let node: Element | null = element
  while (node && node !== document.body) {
    if (getComputedStyle(node).position === 'fixed') return true
    node = node.parentElement
  }
  return false
}

/** Fait remonter le champ au-dessus du clavier (mode app). */
export function scrollIntoViewOnFocus(element: HTMLElement): void {
  if (!isWebviewLayout() || isInsideFixedLayer(element)) return
  element.addEventListener('focus', () => {
    window.setTimeout(() => element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' }), 300)
  }, { once: true })
}

/** safeFocus : pas de focus programmatique dans l'app (clavier intempestif). */
export function safeFocus(element: HTMLElement | null | undefined): void {
  if (element && !isWebviewLayout()) {
    try { element.focus() } catch { /* ignore */ }
  }
}
